import clsx from 'clsx';
import { ComponentProps, RefObject } from 'react';
import { AltaAppSwitcher } from '@msi-alta/alta-app-switcher';
import opConfig from 'openpathConfig';

import { OpHeader } from 'new-components/OpHeader/OpHeader';
import { UserMenu } from 'new-components/UserMenu/UserMenu';
import { OrgSwitcher } from 'new-components/OrgSwitcher/OrgSwitcher';
import type { OrgSwitcherProps } from 'new-components/OrgSwitcher/types';
import { OpButton } from 'new-components/DLS/OpButton/OpButton';
import { HamburgerMenuSvg } from 'components/svgs/HamburgerMenuSvg';
import { AppSwitcher } from 'new-components/AppSwitcher/AppSwitcher';
import { HelpMenu } from 'new-components/HelpMenu/HelpMenu';
import { CobaltChatButton } from 'new-components/CobaltChatButton/CobaltChatButton';
import { useCobaltChatContext } from 'routes/AppContainer';
import { LockdownSelector } from 'new-components/LockdownSelector/LockdownSelector';
import { useOpQuery } from 'utils/customHooks/useOpQuery';
import { getOpParams } from 'utils/getOpParams';
import { getAccessToken } from 'utils/accessToken';
import { useIdentityAndUser } from 'utils/customHooks/useIdentityAndUser';

import '@msi-alta/alta-app-switcher/index.css';
import './OpAppHeader.scss';

interface OpAppHeaderProps extends ComponentProps<typeof OpHeader> {
  appSwitcherRef?: RefObject<HTMLSpanElement>;
  homePath?: string;
  onHamburgerClick?: () => void;
  orgSwitcherProps?: OrgSwitcherProps;
  showHamburger?: boolean;
}

export const OpAppHeader = ({
  appSwitcherRef,
  className,
  homePath,
  onHamburgerClick,
  showHamburger,
  orgSwitcherProps,
  ...headerProps
}: OpAppHeaderProps) => {
  const { orgId } = getOpParams();
  const { data: isUnified } = useOpQuery({
    apiEndpointName: 'describeOrg',
    parameters: [orgId],
    select: (data) => data.json.data.isUnified,
    suppressErrorMessage: true,
  });

  const isMasterMode = window.location.pathname.includes('/master');
  const { identityId } = useIdentityAndUser();

  const { data: colorTheme } = useOpQuery({
    apiEndpointName: 'describeIdentityPreferenceSet',
    parameters: [identityId!],
    select: (response) => response.json.data.colorTheme,
  });

  const accessToken = getAccessToken();
  const { isChatOpen, toggleChat } = useCobaltChatContext();

  return (
    <OpHeader className={clsx('op-app-header', className)} {...headerProps}>
      <div className="op-app-header__content">
        <div className="op-app-header__left-content">
          {showHamburger && (
            <OpButton
              className="op-app-header__hamburger"
              onClick={onHamburgerClick}
              icon={<HamburgerMenuSvg />}
              gtm="op-app-header-hamburger"
              data-testid="op-app-header-hamburger"
            />
          )}
          {(orgSwitcherProps?.visible ?? true) && (
            <OrgSwitcher homePath={homePath} {...orgSwitcherProps} />
          )}
        </div>
        <div className="op-app-header__right-content">
          <LockdownSelector />
          <CobaltChatButton onClick={toggleChat} isActive={isChatOpen} />
          <HelpMenu />
          <span ref={appSwitcherRef}>
            {isMasterMode || isUnified ? (
              <AltaAppSwitcher
                token={accessToken}
                orgId={orgId}
                theme={colorTheme || 'light'}
                region={opConfig.APP_SWITCHER_REGION_OVERRIDE || opConfig.ENV}
              />
            ) : (
              <AppSwitcher />
            )}
          </span>
          <UserMenu />
        </div>
      </div>
    </OpHeader>
  );
};
